import {
  Code2,
  Smartphone,
  Palette,
  Server,
  Bot,
  Cloud,
  ShoppingCart,
  Database,
  CheckCircle2
} from "lucide-react";

const servicesData = {
  "web-development": {
    icon: Code2,
    number: "01",
    title: "Web Development",
    shortTitle: "Modern Web Experiences",
    description:
      "We design and develop modern, responsive and scalable websites and web applications that help businesses build a strong digital presence and deliver better customer experiences.",

    overview:
      "A website should be more than an online presence. It should communicate your brand, support your business goals and give your customers a smooth digital experience. At Nexis Fold, we build websites and web applications with clean interfaces, responsive layouts and scalable architecture.",

    services: [
      {
        title: "Business Websites",
        description:
          "Professional websites designed to communicate your brand, services and value clearly."
      },
      {
        title: "Web Applications",
        description:
          "Interactive and functional web applications built around specific business requirements."
      },
      {
        title: "Responsive Development",
        description:
          "Web experiences optimized for desktop, tablet and mobile devices."
      },
      {
        title: "Custom Features",
        description:
          "Custom functionality designed around your workflows, customers and business processes."
      }
    ],

    technologies: [
      "React",
      "JavaScript",
      "HTML5",
      "CSS3",
      "REST APIs",
      "MySQL",
      "MongoDB"
    ],

    process: [
      "Requirement Discovery",
      "UI Planning",
      "Development",
      "Testing",
      "Deployment"
    ]
  },

  "mobile-development": {
    icon: Smartphone,
    number: "02",
    title: "Mobile App Development",
    shortTitle: "Mobile Experiences",
    description:
      "We build intuitive mobile applications designed to provide smooth experiences across modern smartphones and connected devices.",

    overview:
      "Mobile applications give businesses a direct connection with their customers. We focus on creating clean interfaces, useful functionality and reliable application experiences that are designed around real user needs.",

    services: [
      {
        title: "Android Applications",
        description:
          "Modern Android applications designed for performance and usability."
      },
      {
        title: "iOS Applications",
        description:
          "Clean and intuitive mobile experiences designed for Apple devices."
      },
      {
        title: "Business Applications",
        description:
          "Custom applications that help businesses manage operations and customer interactions."
      },
      {
        title: "API Integration",
        description:
          "Mobile applications connected with secure backend services and APIs."
      }
    ],

    technologies: [
      "React Native",
      "JavaScript",
      "REST APIs",
      "Node.js",
      "MySQL",
      "MongoDB"
    ],

    process: [
      "Product Planning",
      "UX Design",
      "Application Development",
      "Testing",
      "Launch & Support"
    ]
  },

  "ui-ux-design": {
    icon: Palette,
    number: "03",
    title: "UI/UX Design",
    shortTitle: "Interfaces People Enjoy",
    description:
      "We create modern interfaces and user experiences that make digital products simple, attractive and easy to use.",

    overview:
      "Good design is not only about appearance. It is about making products understandable and useful. Our design process combines visual design, user experience and business requirements to create interfaces that feel natural to use.",

    services: [
      {
        title: "UI Design",
        description:
          "Modern visual interfaces with consistent layouts, typography and design systems."
      },
      {
        title: "UX Design",
        description:
          "User journeys and experiences designed around real customer needs."
      },
      {
        title: "Responsive Design",
        description:
          "Interfaces that work smoothly across desktop, tablet and mobile screens."
      },
      {
        title: "Design Systems",
        description:
          "Reusable components and visual systems that maintain consistency across products."
      }
    ],

    technologies: [
      "Figma",
      "Wireframing",
      "Prototyping",
      "Design Systems",
      "Responsive Design",
      "User Research"
    ],

    process: [
      "Research",
      "Wireframes",
      "Visual Design",
      "Prototype",
      "Developer Handoff"
    ]
  },

  "backend-development": {
    icon: Server,
    number: "04",
    title: "Backend Development",
    shortTitle: "Reliable Systems Behind Products",
    description:
      "We develop secure backend systems, APIs and database architectures that power reliable and scalable digital products.",

    overview:
      "A strong backend is the foundation of a reliable digital product. We build structured backend systems that manage business logic, data, authentication and communication between applications and services.",

    services: [
      {
        title: "REST API Development",
        description:
          "Structured APIs that allow applications and services to communicate efficiently."
      },
      {
        title: "Database Development",
        description:
          "Reliable database structures designed for secure and organized data management."
      },
      {
        title: "Authentication",
        description:
          "Secure login, authorization and protected application functionality."
      },
      {
        title: "Business Logic",
        description:
          "Backend systems designed around real business workflows and requirements."
      }
    ],

    technologies: [
      "Node.js",
      "Express.js",
      "Java",
      "Spring Boot",
      "MySQL",
      "MongoDB",
      "REST API"
    ],

    process: [
      "Architecture Planning",
      "Database Design",
      "API Development",
      "Testing",
      "Deployment"
    ]
  },

  "ai-machine-learning": {
    icon: Bot,
    number: "05",
    title: "AI & Machine Learning",
    shortTitle: "Intelligent Software Solutions",
    description:
      "We develop practical AI and machine learning solutions that turn business data into useful predictions, classifications and intelligent experiences.",

    overview:
      "Artificial intelligence can help businesses make better decisions, automate analysis and build smarter products. We focus on practical AI solutions where machine learning provides measurable value.",

    services: [
      {
        title: "Machine Learning Solutions",
        description:
          "Models designed to identify patterns and generate useful predictions from data."
      },
      {
        title: "Classification Systems",
        description:
          "AI systems that classify images, documents, records and other business data."
      },
      {
        title: "AI-Powered Applications",
        description:
          "Applications enhanced with intelligent features and machine learning capabilities."
      },
      {
        title: "AI Integration",
        description:
          "Integration of AI capabilities into existing websites, applications and workflows."
      }
    ],

    technologies: [
      "Python",
      "Machine Learning",
      "Deep Learning",
      "NLP",
      "Computer Vision",
      "APIs",
      "Data Processing"
    ],

    process: [
      "Problem Definition",
      "Data Preparation",
      "Model Development",
      "Evaluation",
      "Integration"
    ]
  },

  "ai-automation": {
    icon: Bot,
    number: "06",
    title: "AI Automation",
    shortTitle: "Automate Work. Improve Efficiency.",
    description:
      "We design intelligent automation systems that connect AI, applications and business workflows to reduce repetitive work and improve efficiency.",

    overview:
      "Businesses spend significant time on repetitive processes. AI automation can connect multiple systems, process information and execute workflows with less manual intervention. We design automation around the actual process rather than simply adding AI for the sake of it.",

    services: [
      {
        title: "Workflow Automation",
        description:
          "Automated workflows that connect business applications and reduce repetitive tasks."
      },
      {
        title: "AI Agents",
        description:
          "Intelligent agents designed to handle specific business tasks and workflows."
      },
      {
        title: "Document Automation",
        description:
          "Systems that process documents, extract information and move data through workflows."
      },
      {
        title: "Business Process Automation",
        description:
          "Automation solutions designed around real operational processes."
      }
    ],

    technologies: [
      "Python",
      "AI",
      "APIs",
      "n8n",
      "Automation",
      "LLMs",
      "Workflow Systems"
    ],

    process: [
      "Workflow Analysis",
      "Automation Planning",
      "AI Integration",
      "Testing",
      "Deployment"
    ]
  },

  "cloud-devops": {
    icon: Cloud,
    number: "07",
    title: "Cloud & DevOps",
    shortTitle: "Reliable Digital Infrastructure",
    description:
      "We help businesses deploy and maintain reliable applications with scalable cloud environments and modern development practices.",

    overview:
      "A good application also needs a reliable environment to run. We help structure deployment environments and development workflows so applications can be delivered efficiently and maintained as they grow.",

    services: [
      {
        title: "Cloud Deployment",
        description:
          "Deployment of websites and applications to modern cloud platforms."
      },
      {
        title: "Application Hosting",
        description:
          "Reliable hosting environments for modern digital applications."
      },
      {
        title: "Deployment Workflows",
        description:
          "Structured development and deployment processes for faster delivery."
      },
      {
        title: "Performance & Scaling",
        description:
          "Infrastructure planning focused on application performance and future growth."
      }
    ],

    technologies: [
      "Cloud Platforms",
      "Docker",
      "Git",
      "CI/CD",
      "Node.js",
      "React",
      "Server Management"
    ],

    process: [
      "Infrastructure Planning",
      "Environment Setup",
      "Deployment",
      "Testing",
      "Monitoring"
    ]
  },

  "ecommerce-development": {
    icon: ShoppingCart,
    number: "08",
    title: "E-Commerce Development",
    shortTitle: "Digital Commerce Experiences",
    description:
      "We create modern e-commerce platforms that make product discovery, shopping and business management simple and effective.",

    overview:
      "A successful e-commerce experience needs more than attractive product pages. Customers need simple navigation, clear information and a smooth purchasing journey. We build commerce experiences around usability, performance and business requirements.",

    services: [
      {
        title: "Online Stores",
        description:
          "Modern online stores designed to showcase products and convert visitors into customers."
      },
      {
        title: "Product Management",
        description:
          "Systems for managing products, categories, pricing and inventory."
      },
      {
        title: "Order Management",
        description:
          "Structured workflows for managing customer orders and business operations."
      },
      {
        title: "Payment Integration",
        description:
          "Integration of suitable payment and checkout systems for digital commerce."
      }
    ],

    technologies: [
      "React",
      "JavaScript",
      "Node.js",
      "REST APIs",
      "MySQL",
      "MongoDB",
      "Payment APIs"
    ],

    process: [
      "Business Analysis",
      "Store Design",
      "Development",
      "Testing",
      "Launch"
    ]
  }
};

export default servicesData;